/**
 * Core Minesweeper Game Types
 *
 * All TypeScript types and interfaces for the Minesweeper puzzle game.
 * Single-player logic puzzle: reveal cells without hitting mines.
 */

// ============================================================================
// Board Configuration
// ============================================================================

/** A single cell on the Minesweeper board */
export interface MinesweeperCell {
  /** Whether this cell contains a mine */
  isMine: boolean;
  /** Whether this cell has been revealed */
  isRevealed: boolean;
  /** Whether this cell has been flagged by the player */
  isFlagged: boolean;
  /** Number of adjacent mines (0-8) */
  adjacentMines: number;
}

/** 2D array of cells */
export type MinesweeperBoard = MinesweeperCell[][];

/** Difficulty levels */
export type MinesweeperDifficulty = 'beginner' | 'intermediate' | 'expert';

// ============================================================================
// Game State
// ============================================================================

export interface MinesweeperGameState {
  /** Current board state */
  board: MinesweeperBoard;
  /** Number of rows */
  rows: number;
  /** Number of columns */
  cols: number;
  /** Total number of mines */
  mineCount: number;
  /** Number of flags placed */
  flagCount: number;
  /** Current game status */
  gameStatus: 'idle' | 'playing' | 'won' | 'lost';
  /** Elapsed time in seconds */
  timer: number;
  /** Whether the first click has been made */
  firstClick: boolean;
  /** The cell that caused game over (row, col), or null */
  deathCell: { row: number; col: number } | null;
  /** Difficulty of the current game */
  difficulty: MinesweeperDifficulty;
  /** Unique game identifier */
  gameId: string;
  /** Timestamp when the game started */
  startedAt: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface MinesweeperGameSettings {
  difficulty: MinesweeperDifficulty;
}

// ============================================================================
// Statistics
// ============================================================================

export interface MinesweeperDifficultyStats {
  gamesPlayed: number;
  gamesWon: number;
  bestTime: number | null;
  averageTime: number;
  winPercentage: number;
}

export interface MinesweeperStatistics {
  userId?: string;
  beginner: MinesweeperDifficultyStats;
  intermediate: MinesweeperDifficultyStats;
  expert: MinesweeperDifficultyStats;
  totalGames: number;
  totalPlayTime: number;
  currentStreak: number;
  bestStreak: number;
}

export interface MinesweeperGameResult {
  gameId: string;
  difficulty: MinesweeperDifficulty;
  won: boolean;
  time: number;
  completedAt: number;
}

// ============================================================================
// Component Props
// ============================================================================

export interface MinesweeperGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface MinesweeperGameModeSelectorProps {
  onStartGame: (settings: MinesweeperGameSettings) => void;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
}

export interface MinesweeperBoardProps {
  board: MinesweeperBoard;
  gameStatus: MinesweeperGameState['gameStatus'];
  deathCell: MinesweeperGameState['deathCell'];
  onCellClick: (row: number, col: number) => void;
  onCellRightClick: (row: number, col: number) => void;
  difficulty: MinesweeperDifficulty;
}

// ============================================================================
// Constants
// ============================================================================

export const DIFFICULTY_CONFIG: Record<
  MinesweeperDifficulty,
  { rows: number; cols: number; mines: number }
> = {
  beginner: { rows: 9, cols: 9, mines: 10 },
  intermediate: { rows: 16, cols: 16, mines: 40 },
  expert: { rows: 16, cols: 30, mines: 99 },
};

/** Number colors by adjacent mine count (1-indexed) */
export const NUMBER_COLORS: Record<number, string> = {
  1: '#0000ff', // blue
  2: '#008000', // green
  3: '#ff0000', // red
  4: '#000080', // dark blue
  5: '#800000', // dark red
  6: '#008080', // teal
  7: '#000000', // black
  8: '#808080', // gray
};
